import React from "react";
import { motion } from "framer-motion";
import { Check, X } from "lucide-react";

const notOurFitExamples = [
  "Looking for just an AI receptionist",
  "Looking to bolt on just one AI feature",
  "Does not want to proactively or actively change-manage their office",
  "We don't believe we can deliver meaningful ROI for the practice",
  "May be disrespectful or unprofessional",
];

const criteria = [
  "We believe you will be able to change and manage the process",
  "We believe we can drive significant ROI for you",
  "You want to run a significant percentage of your practice on autopilot",
  "Your office is professional and empathetic — a positive working relationship for our account managers",
];

const WhoWeTakeOnSlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-6 xl:p-8 relative overflow-hidden font-sans">
      {/* Background blobs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[120px] -translate-y-1/3 translate-x-1/4" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-indigo-100/40 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4" />
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-40" />

      <div className="max-w-[1240px] w-full mx-auto flex flex-col h-full relative z-10 justify-center">
        {/* Title */}
        <div className="text-center mb-8 flex-shrink-0">
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl lg:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.08]"
          >
            Which types of clients do we{" "}
            <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 bg-clip-text text-transparent">
              typically take on?
            </span>
          </motion.h2>
        </div>

        {/* Two buckets — Not our fit / Our fit */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-5 items-stretch"
        >
          {/* Not our fit */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_12px_32px_-16px_rgba(15,23,42,0.1)] p-6 flex flex-col">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center">
                <X className="w-5 h-5 text-slate-400" />
              </div>
              <h3 className="text-[20px] font-bold text-slate-900 leading-tight">
                Clients We Do Not Take On
              </h3>
            </div>
            <div className="space-y-3 mb-4">
              {notOurFitExamples.map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <X className="w-4 h-4 text-slate-400 shrink-0 mt-[5px]" />
                  <span className="text-[16px] text-slate-700 leading-snug">
                    {item}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-auto pt-4 border-t border-slate-100">
              <p className="text-[14.5px] text-slate-600 leading-relaxed italic">
                If for any reason we decide we cannot take you on, we will
                refer you to another vendor that we hold in high regard.
              </p>
            </div>
          </div>

          {/* Our fit — now contains the criteria */}
          <div className="bg-white rounded-2xl border-2 border-blue-200 shadow-[0_12px_32px_-16px_rgba(59,130,246,0.18)] p-6 flex flex-col relative overflow-hidden">
            <div className="absolute top-3 right-3">
              <span className="bg-blue-600 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full shadow-sm uppercase tracking-widest">
                Our fit
              </span>
            </div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
                <Check className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-[20px] font-bold text-slate-900 leading-tight">
                Clients We Likely Accept
              </h3>
            </div>

            <div className="space-y-3">
              {criteria.map((c, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-blue-500 shrink-0 mt-[5px]" />
                  <span className="text-[16px] text-slate-700 leading-snug">
                    {c}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WhoWeTakeOnSlide;
